import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { setupAppPageBodyBackground } from "../game/gameStageBackground";
import {
  STAGE_ROW_KEY,
  ensureStageRowId,
  ensureStageSessionForUser,
  saveGameStageAndReport,
} from "../../functions/stageReportSync";
import StageTimer from "../game/StageTimer";
import {
  formatSecondsToClock,
  loadHallElapsedFromDb,
  persistHallElapsedTime,
  readTotalScoreFromStage,
  liveProgressPoints,
} from "../hall_1/hallTimer";
import {
  markHall2HotspotCompleted,
  unlockHall2Hotspot,
  getRedirectIfHall2StageDone,
} from "./heritageHallData";
import {
  JOURNAL_POINTS,
  JOURNAL_SUCCESS_COPY,
  JOURNAL_UNLOCKS,
} from "./restorationJournalData";
import UvJournal from "./UvJournal";
import deskBg from "./stage8/stage8_desk.jpg";
import mobBg from "./stage8/stage8_mob.jpg";
import messageBox from "./stage8/box.png";
import letterBg from "../img/letter.png";
import greenBtn from "../img/green_button.png";
import "../fonts/breuer-headline.css";
import "../game/stageSuccessLetter.css";
import "./stage8.css";

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

/** Hall 2 Stage 8 — Restoration Journal (UV torch). */
export default function HallStage8() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { user } = useSelector((s) => s.auth);
  const storedUser = useMemo(() => user || getStoredUser(), [user]);
  const isDemoBypass = storedUser?.sessionId === "demobypass";
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const [status, setStatus] = useState("playing");
  const [errorMsg, setErrorMsg] = useState("");
  const [elapsedSec, setElapsedSec] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [scanProgress, setScanProgress] = useState(0);
  const [timerReady, setTimerReady] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || ""
  );

  const elapsedRef = useRef(0);
  const completingRef = useRef(false);

  const liveStagePoints = useMemo(
    () => Math.round(Math.min(1, Math.max(0, scanProgress)) * JOURNAL_POINTS),
    [scanProgress]
  );

  const displayPoints =
    status === "success" ? totalScore : totalScore + liveStagePoints;

  useEffect(() => {
    dispatch(setBackButtonUrl("/hall-2"));
  }, [dispatch]);

  useEffect(() => {
    document.body.classList.add("hall2-stage8-page");
    const cleanupBg = setupAppPageBodyBackground(document.body, {
      deskForeground: deskBg,
      mobForeground: mobBg,
    });
    return () => {
      cleanupBg?.();
      document.body.classList.remove("hall2-stage8-page");
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      ensureStageSessionForUser(storedUser);
      const { stageId, elapsedSec: sec, stage } = await loadHallElapsedFromDb({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
      });
      if (cancelled) return;

      const redirect = getRedirectIfHall2StageDone(stage, 8);
      if (redirect) {
        navigate(redirect, { replace: true });
        return;
      }

      if (stageId) setStageRowId(String(stageId));
      elapsedRef.current = sec;
      setElapsedSec(sec);
      setTotalScore(readTotalScoreFromStage(stage));
      setTimerReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, storedUser, isDemoBypass, navigate]);

  useEffect(() => {
    if (!timerReady || status === "success") return undefined;
    const tick = window.setInterval(() => {
      setElapsedSec((s) => {
        const next = s + 1;
        elapsedRef.current = next;
        return next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [timerReady, status]);

  useEffect(() => {
    if (!timerReady) return undefined;
    const save = () => {
      persistHallElapsedTime({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId: stageRowId || sessionStorage.getItem(STAGE_ROW_KEY),
        elapsedSec: elapsedRef.current,
      });
    };
    const id = window.setInterval(save, 15000);
    return () => {
      clearInterval(id);
      if (status === "playing") save();
    };
  }, [
    timerReady,
    backendBase,
    adminToken,
    storedUser,
    isDemoBypass,
    stageRowId,
    status,
  ]);

  const completePuzzle = useCallback(async () => {
    if (completingRef.current || status === "success" || status === "saving") {
      return;
    }
    completingRef.current = true;
    setStatus("saving");
    setErrorMsg("");

    const timeStr = formatSecondsToClock(elapsedRef.current);
    const answers = {
      puzzle: "restoration_journal",
      revealed: ["sketch", "story", "archive"],
    };

    try {
      let stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      if (!stageId && !isDemoBypass && backendBase && adminToken) {
        stageId = await ensureStageRowId({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
        });
        if (stageId) setStageRowId(String(stageId));
      }

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        const saveResult = await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: 8,
          score: JOURNAL_POINTS,
          answers,
          time: timeStr,
        });
        if (!saveResult?.ok) {
          throw new Error("stage8 save failed");
        }
        if (saveResult.total_score != null) {
          setTotalScore(Number(saveResult.total_score) || 0);
        } else {
          setTotalScore((prev) => prev + JOURNAL_POINTS);
        }
      } else {
        setTotalScore((prev) => prev + JOURNAL_POINTS);
      }

      markHall2HotspotCompleted("restore_journal");
      unlockHall2Hotspot(JOURNAL_UNLOCKS);
      setStatus("success");
    } catch (err) {
      console.error("Restoration journal save:", err);
      completingRef.current = false;
      setRetryKey((k) => k + 1);
      setScanProgress(0);
      setStatus("playing");
      setErrorMsg("Could not save progress. Sweep the notes again, then retry.");
    }
  }, [
    status,
    stageRowId,
    isDemoBypass,
    backendBase,
    adminToken,
    storedUser,
  ]);

  return (
    <div
      className={`hall2-stage8 journal-play${
        status === "success" ? " journal-play--success" : ""
      }`}
    >
      <div className="stage-escape-hud">
        <StageTimer
          timeLabel={formatSecondsToClock(elapsedSec)}
          points={displayPoints}
        />
      </div>

      <div className="journal-play__inner">
        <header className="journal-play__header">
          <h1 className="journal-play__title">
            {status === "success"
              ? "PUZZLE 2 — GAMEPLAY"
              : "RESTORATION JOURNAL (UV)"}
          </h1>
          <div className="journal-play__rule" aria-hidden>
            <span />
          </div>
        </header>

        <div className="journal-play__board">
          {status !== "success" ? (
            <aside
              className="journal-play__hint"
              style={{ backgroundImage: `url(${messageBox})` }}
            >
              <p>The Chief Conservator's journal contains hidden restoration notes.
Use the UV torch to reveal them.
</p>
            </aside>
          ) : null}

          <div className="journal-play__stage">
            <UvJournal
              key={retryKey}
              disabled={status === "saving" || status === "success"}
              onComplete={completePuzzle}
              onScanChange={setScanProgress}
            />
          </div>

          {status === "success" ? (
            <aside
              className="journal-play__success stage-success-letter"
              style={{ backgroundImage: `url(${letterBg})` }}
            >
              <h2 className="journal-play__success-title">
                {JOURNAL_SUCCESS_COPY.title}
              </h2>
              <div className="journal-play__rule journal-play__success-rule" aria-hidden>
                <span />
              </div>
              <p className="journal-play__success-body">
                {JOURNAL_SUCCESS_COPY.body}
              </p>
              <button
                type="button"
                className="journal-play__success-cta"
                style={{ backgroundImage: `url(${greenBtn})` }}
                onClick={() => navigate("/hall-2")}
              >
                CONTINUE
              </button>
            </aside>
          ) : null}
        </div>

        {errorMsg ? (
          <p className="journal-play__error" role="alert">
            {errorMsg}
          </p>
        ) : null}

        {status === "saving" ? (
          <p className="journal-play__saving" aria-live="polite">
            Recording discoveries…
          </p>
        ) : null}
      </div>
    </div>
  );
}
